import { DataType, ElementType, BodyType } from "../types/types";

/**
 * Converts old PHP page JSON format to the new React DataType format.
 * Used at runtime when no hardcoded template exists for a page ID.
 */

const MASK_URL_MAP: Record<string, string> = {
  "car-01-AVG": "carAVG",
  "car-01-AVD": "carAVD",
  "car-01-ARD": "carARD",
  "car-01-ARG": "carARG",
  "car-01-AV": "carAV",
  "car-01-AR": "carAR",
  "car-01-LG": "carLG",
  "car-01-LD": "carLD",
};

function extractMaskName(maskUrl: string): string {
  if (!maskUrl) return "";
  for (const [pattern, name] of Object.entries(MASK_URL_MAP)) {
    if (maskUrl.includes(pattern)) return name;
  }
  const basename = maskUrl.split("/").pop()?.split(".")[0]?.toUpperCase() || "";
  if (["AVG", "AVD", "ARD", "ARG", "AV", "AR", "LG", "LD"].includes(basename)) {
    return `car${basename}`;
  }
  return maskUrl;
}

function toBool(val: any): boolean {
  if (typeof val === "boolean") return val;
  if (typeof val === "string") return val.trim() === "1" || val.trim() === "true";
  return !!val;
}

type TitleMarker = { __page_title__: string };

function convertElement(
  elementKey: string,
  elementValue: any
): (ElementType | TitleMarker)[] {
  const parts = elementKey.split("-");
  if (parts.length < 2) return [];
  const elementType = parts.slice(1).join("-");
  const val = elementValue;

  switch (elementType) {
    case "title":
      return [
        {
          __page_title__: Array.isArray(val) ? val[0] : val,
        } as TitleMarker,
      ];

    case "titleCondition":
    case "paragraphCondition":
    case "imageCondition":
      return [];

    case "paragraph": {
      const text = Array.isArray(val) ? val[0] : val;
      return [{ type: "Paragraph", text }];
    }

    case "paragraphUrl":
      return [{ type: "Link", text: val[0], url: val[1] }];

    case "picture":
      // [name, image, button, time, geolocation, mandatory, onPDF, inpdf, type]
      return [
        {
          type: "ButtonPicture",
          isLabelOptionnal: false,
          title: val[0],
          required: val.length > 5 ? toBool(val[5]) : false,
          value: "",
          iconUrl: typeof val[1] === "string" ? val[1] : undefined,
        },
      ];

    case "pictureMask":
      // [name, mask, image, button, time, geolocation, mandatory, onPDF, inpdf, type]
      return [
        {
          type: "ButtonPictureMask",
          isLabelOptionnal: false,
          title: val[0],
          required: val.length > 6 ? toBool(val[6]) : false,
          value: "",
          mask: extractMaskName(val[1]),
          iconUrl: typeof val[2] === "string" ? val[2] : undefined,
        },
      ];

    case "listPicture":
      // [name, image, button, time, geolocation, onPDF, inpdf, type]
      return [
        {
          type: "ButtonListPicture",
          isLabelOptionnal: false,
          title: val[0],
          required: false,
          value: "",
          iconUrl: typeof val[1] === "string" ? val[1] : undefined,
        },
      ];

    case "document": {
      // [name, image, button, mandatory, onPDF, inpdf, type]
      const mandatory = val.length > 3 ? toBool(val[3]) : false;
      const elem: ElementType = {
        type: "ButtonDocument",
        title: val[0],
        valid: "Document ajouté",
        iconUrl: typeof val[1] === "string" ? val[1] : undefined,
      };
      if (mandatory) elem.required = true;
      return [elem];
    }

    case "input": {
      // [name, mandatory, inpdf]
      const mandatory = val.length > 1 ? toBool(val[1]) : false;
      return [
        {
          type: "InputText",
          title: val[0],
          placeholder: `${val[0]}...`,
          required: mandatory,
        },
      ];
    }

    case "textarea": {
      const mandatory = val.length > 1 ? toBool(val[1]) : false;
      return [
        {
          type: "Textarea",
          title: val[0],
          placeholder: `${val[0]}...`,
          required: mandatory,
        },
      ];
    }

    case "checkbox": {
      const mandatory = val.length > 1 ? toBool(val[1]) : false;
      return [{ type: "Checkbox", text: val[0], required: mandatory }];
    }

    case "radio": {
      // [name, mandatory, inpdf, type_document, type_document_true]
      const elem: ElementType = {
        type: "YesNo",
        text: val[0],
      };
      if (val.length > 1 && toBool(val[1])) elem.required = true;
      if (val.length > 3 && val[3]) elem.type_false = String(val[3]);
      if (val.length > 4 && val[4]) {
        elem.type_true = String(val[4]);
        elem.isSpecialFileType = true;
        elem.isCheckFalse = true;
        elem.defaultFalse = false;
      }
      return [elem];
    }

    case "checkbox1": {
      // radioModule: [name, mandatory, inpdf, data(semicolon)]
      let name: string, data: string;
      if (val.length >= 4 && typeof val[3] === "string" && val[3].includes(";")) {
        name = val[0];
        data = val[3];
      } else if (val.length >= 4) {
        name = val[1];
        data = val[0];
      } else {
        return [];
      }
      return data
        .split(";")
        .filter((o: string) => o.trim())
        .map((option: string) => ({
          type: "YesNo" as const,
          text: name !== option.trim() ? `${name} - ${option.trim()}` : option.trim(),
        }));
    }

    case "checkbox2": {
      let name: string, data: string;
      if (val.length >= 4 && typeof val[3] === "string" && val[3].includes(";")) {
        name = val[0];
        data = val[3];
      } else if (val.length >= 4) {
        name = val[1];
        data = val[0];
      } else {
        return [];
      }
      return data
        .split(";")
        .filter((o: string) => o.trim())
        .map((option: string) => ({
          type: "Checkbox" as const,
          text: name !== option.trim() ? `${name} - ${option.trim()}` : option.trim(),
        }));
    }

    case "signature": {
      const mandatory = val.length > 1 ? toBool(val[1]) : false;
      return [
        {
          type: "ButtonSignature",
          isLabelOptionnal: false,
          required: mandatory,
        },
      ];
    }

    case "video": {
      const mandatory = val.length > 3 ? toBool(val[3]) : false;
      return [
        {
          type: "ButtonPicture",
          isLabelOptionnal: false,
          title: val[0],
          required: mandatory,
          value: "",
        },
      ];
    }

    case "specialWindow":
      return [{ type: "Link", text: val[0], url: val[1] }];

    case "text":
    case "basic": {
      if (Array.isArray(val) && val.length >= 2) {
        return [
          {
            type: "InputText",
            title: val[0],
            placeholder: `${val[0]}...`,
            required: val.length > 1 ? toBool(val[1]) : false,
          },
        ];
      }
      if (typeof val === "string") {
        return [{ type: "Paragraph", text: val }];
      }
      return [];
    }

    // Skipped: image, variable, visio, visioconference, buttonBack, buttonNext, pageNext
    default:
      return [];
  }
}

function isTitle(elem: ElementType | TitleMarker): elem is TitleMarker {
  return "__page_title__" in elem;
}

function pageSortKey(key: string): number {
  if (key.startsWith("page-")) {
    const num = parseInt(key.split("-")[1], 10);
    return isNaN(num) ? 9999 : num;
  }
  return 10000;
}

/**
 * Convert old PHP page data to new React DataType format.
 * @param pageData - The raw JSON object from the old `page.data` column
 * @param pageName - Optional page name for fallback title
 */
export function convertOldPageData(
  pageData: Record<string, any> | null | undefined,
  pageName: string = ""
): DataType {
  if (!pageData || typeof pageData !== "object") {
    return { header: { texts: [], variables: [] }, body: [], isTime: false };
  }
  const result: DataType = {
    header: { texts: [], variables: [] },
    body: [],
  };

  // Collect all elements across all page-N sections in order
  const allElements: [string, any][] = [];
  const sortedKeys = Object.keys(pageData)
    .filter((k) => k.startsWith("page-"))
    .sort((a, b) => pageSortKey(a) - pageSortKey(b));

  for (const key of sortedKeys) {
    const section = pageData[key];
    const sortedElems = Object.entries(section).sort(
      ([a], [b]) =>
        (parseInt(a.split("-")[0], 10) || 9999) -
        (parseInt(b.split("-")[0], 10) || 9999)
    );
    for (const entry of sortedElems) {
      allElements.push(entry as [string, any]);
    }
  }

  // Split by title elements into sections
  const sections: { title: string; elements: ElementType[] }[] = [];
  let currentTitle: string | null = null;
  let currentElements: ElementType[] = [];
  const headerParagraphs: string[] = [];
  let firstTitleSeen = false;
  let firstSectionDataSeen = false;

  for (const [elemKey, elemValue] of allElements) {
    const converted = convertElement(elemKey, elemValue);

    for (const elem of converted) {
      if (isTitle(elem)) {
        if (firstTitleSeen && (currentElements.length || currentTitle)) {
          sections.push({
            title: currentTitle || "Page",
            elements: currentElements,
          });
          currentElements = [];
        }
        currentTitle = elem.__page_title__;
        firstTitleSeen = true;
        firstSectionDataSeen = false;
      } else {
        const typedElem = elem as ElementType;
        if (
          typedElem.type === "Paragraph" &&
          firstTitleSeen &&
          !firstSectionDataSeen &&
          sections.length === 0
        ) {
          headerParagraphs.push(typedElem.text || "");
        } else {
          if (typedElem.type !== "Paragraph") {
            firstSectionDataSeen = true;
          }
          currentElements.push(typedElem);
        }
      }
    }
  }

  // Last section
  if (currentElements.length || currentTitle) {
    sections.push({
      title: currentTitle || pageName || "Page",
      elements: currentElements,
    });
  }

  result.header.texts = headerParagraphs;

  for (const section of sections) {
    if (section.elements.length) {
      result.body.push(section as BodyType);
    }
  }

  return result;
}
